import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, ButtonGroup, Modal, Spinner } from "react-bootstrap";
import { XLg } from "react-bootstrap-icons";

import { routes } from "../../Router";
import { deleteLeadSearch } from "../../api/LeadSearch";

const LeadSearchDeleteModal = (props: {
  leadSearch: ILeadSearch;
  onDelete: (leadSearchId: string) => void;
}) => {
  /**
   * A modal for deleting a lead-search.
   */
  const [show, setShow] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleDeleteLeadSearch = async (leadSearch: ILeadSearch) => {
    /**
     * Handle deleting a lead-search.
     */
    setIsDeleting(true);

    await deleteLeadSearch(leadSearch.id).then(() => {
      handleClose();
      // A short delay (300ms; Bootstrap's default) to first allow the modal to close smoothly.
      setTimeout(() => {
        setIsDeleting(false);
        // Call the onDelete callback to remove the lead-search from the (parent element's) list.
        props.onDelete(leadSearch.id);
      }, 300);
    });
  };

  return (
    <>
      <Button
        title="Zoekopdracht verwijderen"
        size="sm"
        variant="outline-danger"
        className="px-2 border-start-0"
        onClick={handleShow}
      >
        <XLg />
      </Button>

      <Modal
        show={show}
        onHide={handleClose}
        centered={true}
        contentClassName="rounded-0"
      >
        <Modal.Header closeButton={true} className="border-bottom-0">
          <Modal.Title>
            <h5 className="mb-0">Zoekopdracht verwijderen?</h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <p className="m-0">
            Zoekopdracht &quot;<b>{props.leadSearch.title}</b>&quot;
            verwijderen?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button
            title="Verwijder"
            variant="danger"
            className="px-4"
            onClick={() => handleDeleteLeadSearch(props.leadSearch)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Spinner animation="border" variant="dark" size="sm" />
            ) : (
              "Verwijder"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default function LeadSearchList(props: {
  leadSearches: ILeadSearch[];
  onLeadSearchDelete: (leadSearchId: string) => void;
}): React.JSX.Element {
  /**
   * A component for listing (and deleting) lead-searches.
   */
  const navigate = useNavigate();

  // Order lead-searches by date (newest first).
  const leadSearchesOrdered = props.leadSearches
    .slice()
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  return (
    <>
      {leadSearchesOrdered.map((leadSearch: ILeadSearch) => (
        <ButtonGroup key={leadSearch.id} className="w-100 mb-1">
          <div className="d-flex w-100">
            <Button
              title={leadSearch.title}
              size="sm"
              variant="outline-primary"
              className="flex-grow-1 text-start text-truncate px-4"
              onClick={() => {
                navigate(routes.leadSearchDetail(leadSearch.id));
              }}
            >
              &quot;{leadSearch.title}&quot; ({leadSearch.dateFormatted})
            </Button>

            <LeadSearchDeleteModal
              leadSearch={leadSearch}
              onDelete={props.onLeadSearchDelete}
            />
          </div>
        </ButtonGroup>
      ))}
    </>
  );
}
